import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { format, subDays } from "date-fns";
import {
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
    AreaChart,
    Area,
} from "recharts";
import QRCodeStyling from "qr-code-styling";
import EmployeeLayoutV2 from "../../components/EmployeeLayoutV2";
import TippingCardDownloadModal from "../../components/TippingCardDownloadModal";
import { useAuth } from "../../context/AuthProvider";
import {
    getBusinessById,
    getEmployeeData,
    getPendingPayoutsByEmployeeID,
    getPayoutsByEmployee,
    getTipsByEmployee,
} from "../../services/firestoreService";

const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || "";
const baseUrl = projectId.includes("test") ? "test.tiply" : "tiply";

const FILTER_OPTIONS = {
    "Last 24 hours": 1,
    "Last 7 days": 7,
    "Last 14 days": 14,
    "Last 30 days": 30,
    "Last 90 days": 90,
    "Last 180 days": 180,
    "Last year": 365,
    "All time": null,
};

const FILTER_LABELS = Object.keys(FILTER_OPTIONS);
const TEAM_TIP_ID = 1;
const TEAM_EMPLOYEE_ID = "team";

const ChevronDown = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 6l4 4 4-4" />
    </svg>
);

const StarIcon = ({ filled }) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill={filled ? "#e8a20e" : "none"} stroke={filled ? "#e8a20e" : "#d4d4d4"} strokeWidth="2">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
);

const CopyIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="9" y="9" width="13" height="13" rx="2" />
        <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
    </svg>
);

const CheckIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 6L9 17l-5-5" />
    </svg>
);

const ExternalIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
        <polyline points="15 3 21 3 21 9" />
        <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
);

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div
            style={{
                background: "#fff",
                border: "1px solid #e8e8ee",
                borderRadius: 12,
                padding: "10px 16px",
                boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
            }}
        >
            <p style={{ color: "#8b8b9e", fontSize: 12, margin: 0 }}>{label}</p>
            <p style={{ color: "#0d7c5f", fontSize: 18, fontWeight: 700, margin: "2px 0 0" }}>
                €{Number(payload[0].value || 0).toFixed(2)}
            </p>
        </div>
    );
};

const getTimestampDate = (value) => {
    if (!value) return null;
    if (value?.seconds) return new Date(value.seconds * 1000);
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatTipDate = (tip) => {
    const date = getTimestampDate(tip?.createdAt);
    return date ? date.toLocaleDateString() : "-";
};

const formatPayoutDate = (payout) => {
    const date = getTimestampDate(payout?.createdAt);
    return date ? date.toLocaleDateString() : "-";
};

export default function EmployeeDashboardV2() {
    const { userData } = useAuth();
    const [employee, setEmployee] = useState(null);
    const [tips, setTips] = useState([]);
    const [payouts, setPayouts] = useState([]);
    const [pendingPayouts, setPendingPayouts] = useState(null);
    const [teamMode, setTeamMode] = useState(false);

    const [filter, setFilter] = useState("Last 30 days");
    const [filterOpen, setFilterOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    const [activeTab, setActiveTab] = useState("tips");
    const [cardModalOpen, setCardModalOpen] = useState(false);

    const filterRef = useRef(null);
    const qrRef = useRef(null);

    const filterDays = FILTER_OPTIONS[filter];

    useEffect(() => {
        const handler = (e) => {
            if (filterRef.current && !filterRef.current.contains(e.target)) {
                setFilterOpen(false);
            }
        };

        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    useEffect(() => {
        let isCancelled = false;

        const fetchData = async () => {
            if (!userData?.businessID || !userData?.employeeID) return;

            const businessData = await getBusinessById(userData.businessID);
            const teamEnabled = !!businessData?.config?.teamEnabled;
            const personalEnabled = !!businessData?.config?.personalEnabled;

            const teamOnly = teamEnabled && !personalEnabled;
            const tipsTargetEmployeeID = teamOnly ? TEAM_EMPLOYEE_ID : userData.employeeID;

            const [employeeData, tipsData, payoutsData, pendingPayoutsData] = await Promise.all([
                getEmployeeData(userData.businessID, userData.employeeID),
                getTipsByEmployee(userData.businessID, tipsTargetEmployeeID),
                getPayoutsByEmployee(userData.businessID, userData.employeeID),
                getPendingPayoutsByEmployeeID(userData.businessID, userData.employeeID),
            ]);

            if (isCancelled) return;

            setEmployee(employeeData || null);
            setTeamMode(teamOnly);
            setTips(Array.isArray(tipsData) ? tipsData : []);
            setPayouts(Array.isArray(payoutsData) ? payoutsData : []);
            setPendingPayouts(pendingPayoutsData || null);

            const qrTipID = teamOnly ? TEAM_TIP_ID : employeeData?.tipID;
            if (qrTipID && qrRef.current) {
                qrRef.current.innerHTML = "";
                const qr = new QRCodeStyling({
                    width: 1000,
                    height: 1000,
                    data: `https://${baseUrl}.ee/tip/${userData.businessID}/${qrTipID}`,
                    image: "/tiply_logo_v4.png",
                    imageOptions: { crossOrigin: "anonymous", margin: 0, imageSize: 0.4 },
                    dotsOptions: { color: "#000000", type: "square" },
                    backgroundOptions: { color: "#ffffff" },
                });

                qr.getRawData("png").then((blob) => {
                    if (isCancelled || !blob) return;
                    const img = document.createElement("img");
                    img.src = URL.createObjectURL(blob);
                    img.alt = "QR Code";
                    img.style.width = "100%";
                    img.style.height = "100%";
                    img.style.objectFit = "contain";
                    img.style.cursor = "pointer";
                    img.onclick = () => {
                        const newTab = window.open();
                        if (newTab) {
                            newTab.document.body.style.margin = "0";
                            newTab.document.body.innerHTML = `<img src="${img.src}" style="width:100%;height:auto;display:block;" />`;
                        }
                    };
                    qrRef.current.innerHTML = "";
                    qrRef.current.appendChild(img);
                });
            }
        };

        fetchData();
        return () => {
            isCancelled = true;
        };
    }, [userData]);

    useEffect(() => {
        if (!copied) return undefined;
        const timer = setTimeout(() => setCopied(false), 2000);
        return () => clearTimeout(timer);
    }, [copied]);

    const filteredTips = useMemo(() => {
        if (!filterDays) return tips;
        const boundary = subDays(new Date(), filterDays);
        return tips.filter((tip) => {
            const tipDate = getTimestampDate(tip.createdAt);
            return tipDate ? tipDate >= boundary : false;
        });
    }, [tips, filterDays]);

    const filteredPayouts = useMemo(() => {
        if (!filterDays) return payouts;
        const boundary = subDays(new Date(), filterDays);
        return payouts.filter((payout) => {
            const payoutDate = getTimestampDate(payout.createdAt);
            return payoutDate ? payoutDate >= boundary : false;
        });
    }, [payouts, filterDays]);

    const totalEarnings = filteredTips.reduce((sum, tip) => sum + Number(tip.amount || 0), 0);
    const paidOut = filteredPayouts.reduce((sum, payout) => sum + Number(payout.totalAmount || 0), 0);
    const pending = Math.max(0, totalEarnings - paidOut);
    const tipCount = filteredTips.length;

    const ratings = filteredTips
        .map((tip) => Number(tip.rating))
        .filter((rating) => !Number.isNaN(rating));
    const avgRating = ratings.length ? (ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(1) : "0.0";

    const uniqueDays = new Set(
        filteredTips
            .map((tip) => getTimestampDate(tip.createdAt))
            .filter(Boolean)
            .map((date) => date.toDateString())
    );

    const avgPerDay = uniqueDays.size ? (totalEarnings / uniqueDays.size).toFixed(2) : "0.00";
    const tipsPerDay = uniqueDays.size ? (filteredTips.length / uniqueDays.size).toFixed(1) : "0.0";

    const chartData = useMemo(() => {
        if (!filteredTips.length) return [];

        if (filterDays === 1) {
            const hourlyMap = {};
            for (let i = 0; i < 24; i += 1) {
                hourlyMap[`${String(i).padStart(2, "0")}:00`] = 0;
            }

            filteredTips.forEach((tip) => {
                const date = getTimestampDate(tip.createdAt);
                if (!date) return;
                const label = `${String(date.getHours()).padStart(2, "0")}:00`;
                if (hourlyMap[label] !== undefined) {
                    hourlyMap[label] += Number(tip.amount || 0);
                }
            });

            return Object.entries(hourlyMap).map(([date, amount]) => ({ date, amount: Number(amount.toFixed(2)) }));
        }

        const sourceDates = filteredTips
            .map((tip) => getTimestampDate(tip.createdAt))
            .filter(Boolean)
            .sort((a, b) => a - b);

        if (!sourceDates.length) return [];

        const startDate = filterDays ? subDays(new Date(), filterDays - 1) : sourceDates[0];
        const endDate = new Date();

        const dailyTipMap = {};
        let cursor = new Date(startDate);
        while (cursor <= endDate) {
            const label = format(cursor, "MMM d");
            dailyTipMap[label] = 0;
            cursor = subDays(cursor, -1);
        }

        filteredTips.forEach((tip) => {
            const date = getTimestampDate(tip.createdAt);
            if (!date) return;
            const label = format(date, "MMM d");
            if (dailyTipMap[label] !== undefined) {
                dailyTipMap[label] += Number(tip.amount || 0);
            }
        });

        return Object.entries(dailyTipMap).map(([date, amount]) => ({
            date,
            amount: Number(amount.toFixed(2)),
        }));
    }, [filteredTips, filterDays]);

    const sortedTips = useMemo(
        () => [...filteredTips].sort((a, b) => (getTimestampDate(b.createdAt) || 0) - (getTimestampDate(a.createdAt) || 0)),
        [filteredTips]
    );

    const sortedPayouts = useMemo(
        () => [...filteredPayouts].sort((a, b) => (getTimestampDate(b.createdAt) || 0) - (getTimestampDate(a.createdAt) || 0)),
        [filteredPayouts]
    );

    const qrTipID = teamMode ? TEAM_TIP_ID : employee?.tipID;
    const tipLink = qrTipID ? `https://${baseUrl}.ee/tip/${userData?.businessID}/${qrTipID}` : "";
    const cardTargetName = employee?.displayName
        || [employee?.firstName, employee?.lastName].filter(Boolean).join(" ");

    const handleCopy = async () => {
        if (!tipLink) return;
        try {
            await navigator.clipboard.writeText(tipLink);
            setCopied(true);
        } catch (err) {
            console.error("Failed to copy text:", err);
        }
    };

    return (
        <EmployeeLayoutV2>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&family=JetBrains+Mono:wght@400;500&display=swap');

                .dashboard-v2 * { box-sizing: border-box; margin: 0; padding: 0; }
                .dashboard-v2 {
                    --bg: #f6f6f9;
                    --surface: #ffffff;
                    --surface-raised: #f0f0f5;
                    --border: #e8e8ee;
                    --border-bright: #d4d4dd;
                    --text-primary: #1a1a2e;
                    --text-secondary: #6b6b82;
                    --text-muted: #9b9bb0;
                    --accent: #0d7c5f;
                    --accent-light: #e8f5f0;
                    --green: #0d7c5f;
                    --green-light: #e8f5f0;
                    --blue: #3b6fe0;
                    --blue-light: #eef3fc;
                    --amber: #c47a0a;
                    --amber-light: #fef6e6;
                    --font: 'DM Sans', -apple-system, sans-serif;
                    --mono: 'JetBrains Mono', monospace;
                    font-family: var(--font);
                    background: var(--bg);
                    color: var(--text-primary);
                    -webkit-font-smoothing: antialiased;
                    min-height: 100vh;
                }
                .dashboard-v2 .dashboard {
                    width: 100%;
                    margin: 0 auto;
                    padding: 32px 0 80px;
                }
                .dashboard-v2 .header {
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    margin-bottom: 24px;
                    gap: 12px;
                    flex-wrap: wrap;
                }
                .dashboard-v2 .header-left h1 { font-size: 28px; font-weight: 700; letter-spacing: -0.5px; }
                .dashboard-v2 .header-left p { color: var(--text-secondary); font-size: 14px; margin-top: 4px; }
                .dashboard-v2 .edit-link {
                    color: var(--accent);
                    font-size: 13px;
                    font-weight: 500;
                    text-decoration: none;
                    padding: 8px 16px;
                    border: 1px solid var(--accent);
                    border-radius: 8px;
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    background: transparent;
                }
                .dashboard-v2 .edit-link:hover { background: var(--accent-light); }
                .dashboard-v2 .warning {
                    border-radius: 12px;
                    border: 1px solid #fecaca;
                    background: #fef2f2;
                    color: #b91c1c;
                    padding: 12px;
                    margin-bottom: 12px;
                    font-size: 14px;
                    font-weight: 600;
                }
                .dashboard-v2 .filter-wrapper { position: relative; margin-bottom: 28px; }
                .dashboard-v2 .filter-btn {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    background: var(--surface);
                    border: 1px solid var(--border);
                    border-radius: 10px;
                    padding: 10px 16px;
                    color: var(--text-primary);
                    font-family: var(--font);
                    font-size: 14px;
                    font-weight: 500;
                    cursor: pointer;
                }
                .dashboard-v2 .filter-btn:hover { border-color: var(--border-bright); background: var(--surface-raised); }
                .dashboard-v2 .filter-dropdown {
                    position: absolute;
                    top: calc(100% + 6px);
                    left: 0;
                    background: var(--surface);
                    border: 1px solid var(--border);
                    border-radius: 12px;
                    padding: 6px;
                    z-index: 50;
                    min-width: 220px;
                    box-shadow: 0 12px 40px rgba(0,0,0,0.1);
                }
                .dashboard-v2 .filter-option {
                    padding: 10px 14px;
                    border-radius: 8px;
                    font-size: 13px;
                    cursor: pointer;
                    color: var(--text-secondary);
                    border: none;
                    background: none;
                    width: 100%;
                    text-align: left;
                    font-family: var(--font);
                }
                .dashboard-v2 .filter-option:hover { background: var(--surface-raised); color: var(--text-primary); }
                .dashboard-v2 .filter-option.active { color: var(--accent); background: var(--accent-light); font-weight: 500; }
                .dashboard-v2 .stats-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 14px;
                    margin-bottom: 28px;
                }
                @media (max-width: 700px) {
                    .dashboard-v2 .stats-grid { grid-template-columns: repeat(2, 1fr); }
                }
                .dashboard-v2 .stat-card {
                    background: var(--surface);
                    border: 1px solid var(--border);
                    border-radius: 16px;
                    padding: 20px;
                    display: flex;
                    flex-direction: column;
                    gap: 14px;
                }
                .dashboard-v2 .stat-card .label {
                    font-size: 12px;
                    font-weight: 500;
                    color: var(--text-muted);
                    text-transform: uppercase;
                    letter-spacing: 0.8px;
                }
                .dashboard-v2 .stat-card .value {
                    font-size: 28px;
                    font-weight: 700;
                    font-family: var(--mono);
                    letter-spacing: -1px;
                    line-height: 1;
                }
                .dashboard-v2 .stat-card .suffix { font-size: 13px; color: var(--text-muted); margin-top: 2px; }
                .dashboard-v2 .icon-dot {
                    width: 36px;
                    height: 36px;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 16px;
                }
                .dashboard-v2 .secondary-stats {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 14px;
                    margin-bottom: 32px;
                }
                @media (max-width: 700px) {
                    .dashboard-v2 .secondary-stats { grid-template-columns: 1fr; }
                }
                .dashboard-v2 .sec-stat {
                    background: var(--surface);
                    border: 1px solid var(--border);
                    border-radius: 14px;
                    padding: 18px 20px;
                    display: flex;
                    align-items: center;
                    gap: 14px;
                }
                .dashboard-v2 .icon-wrap {
                    width: 42px;
                    height: 42px;
                    border-radius: 11px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 18px;
                    flex-shrink: 0;
                }
                .dashboard-v2 .sec-label { font-size: 12px; color: var(--text-muted); margin-bottom: 2px; }
                .dashboard-v2 .sec-value { font-size: 18px; font-weight: 600; font-family: var(--mono); }
                .dashboard-v2 .chart-section {
                    background: var(--surface);
                    border: 1px solid var(--border);
                    border-radius: 18px;
                    padding: 24px;
                    margin-bottom: 28px;
                }
                .dashboard-v2 .chart-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 20px;
                }
                .dashboard-v2 .chart-header h2 { font-size: 16px; font-weight: 600; }
                .dashboard-v2 .chart-total { font-family: var(--mono); font-size: 14px; color: var(--accent); font-weight: 500; }
                .dashboard-v2 .qr-section {
                    background: var(--surface);
                    border: 1px solid var(--border);
                    border-radius: 18px;
                    padding: 28px;
                    margin-bottom: 28px;
                    display: flex;
                    align-items: center;
                    gap: 32px;
                }
                @media (max-width: 700px) {
                    .dashboard-v2 .qr-section { flex-direction: column; text-align: center; }
                }
                .dashboard-v2 .qr-placeholder {
                    width: 160px;
                    height: 160px;
                    background: var(--surface-raised);
                    border-radius: 14px;
                    flex-shrink: 0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 12px;
                    border: 1px solid var(--border);
                    overflow: hidden;
                }
                .dashboard-v2 .qr-info h3 { font-size: 18px; font-weight: 600; margin-bottom: 6px; }
                .dashboard-v2 .qr-info p { color: var(--text-secondary); font-size: 13px; margin-bottom: 16px; line-height: 1.5; }
                .dashboard-v2 .qr-card-download-btn {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    margin-top: 12px;
                    padding: 10px 14px;
                    border: 1px solid var(--accent);
                    border-radius: 10px;
                    background: var(--accent);
                    color: #fff;
                    font-family: var(--font);
                    font-size: 13px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: background 0.15s ease, transform 0.15s ease;
                }
                .dashboard-v2 .qr-card-download-btn:hover:not(:disabled) { background: #09674f; transform: translateY(-1px); }
                .dashboard-v2 .qr-card-download-btn:disabled { cursor: not-allowed; opacity: 0.5; }
                .dashboard-v2 .copy-field {
                    display: flex;
                    align-items: center;
                    background: var(--surface-raised);
                    border: 1px solid var(--border);
                    border-radius: 10px;
                    overflow: hidden;
                    max-width: 420px;
                }
                @media (max-width: 700px) {
                    .dashboard-v2 .copy-field {
                        max-width: 100%;
                        flex-direction: column;
                    }
                    .dashboard-v2 .copy-url {
                        width: 100%;
                        white-space: normal;
                        overflow-wrap: anywhere;
                        word-break: break-word;
                        text-align: left;
                    }
                    .dashboard-v2 .copy-btn {
                        width: 100%;
                        justify-content: center;
                    }
                }
                .dashboard-v2 .copy-url {
                    padding: 10px 14px;
                    font-family: var(--mono);
                    font-size: 13px;
                    color: var(--text-secondary);
                    flex: 1;
                    min-width: 0;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                .dashboard-v2 .copy-btn {
                    padding: 10px 16px;
                    background: var(--accent);
                    border: none;
                    color: #fff;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    font-size: 13px;
                    font-family: var(--font);
                    font-weight: 500;
                    white-space: nowrap;
                }
                .dashboard-v2 .copy-btn:hover { background: #0a6a50; }
                .dashboard-v2 .pending-banner {
                    background: var(--amber-light);
                    border: 1px solid #f0deb3;
                    border-radius: 18px;
                    padding: 24px;
                    margin-bottom: 28px;
                }
                .dashboard-v2 .pending-banner-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 16px;
                }
                .dashboard-v2 .pending-banner-header h3 { font-size: 15px; font-weight: 600; color: var(--amber); }
                .dashboard-v2 .pending-total { font-family: var(--mono); font-size: 22px; font-weight: 700; color: var(--amber); }
                .dashboard-v2 .pending-list { display: flex; flex-direction: column; gap: 8px; }
                .dashboard-v2 .pending-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 10px 14px;
                    background: rgba(255,255,255,0.7);
                    border-radius: 10px;
                    font-size: 13px;
                    border: 1px solid #f0deb3;
                }
                .dashboard-v2 .p-amount { font-family: var(--mono); font-weight: 500; }
                .dashboard-v2 .p-date { color: var(--text-muted); font-size: 12px; }
                .dashboard-v2 .tabs-header {
                    display: flex;
                    border-bottom: 2px solid var(--border);
                }
                .dashboard-v2 .tab-btn {
                    padding: 12px 24px;
                    background: none;
                    border: none;
                    color: var(--text-muted);
                    font-family: var(--font);
                    font-size: 14px;
                    font-weight: 500;
                    cursor: pointer;
                    position: relative;
                }
                .dashboard-v2 .tab-btn.active { color: var(--accent); }
                .dashboard-v2 .tab-btn.active::after {
                    content: '';
                    position: absolute;
                    bottom: -2px;
                    left: 0;
                    right: 0;
                    height: 2px;
                    background: var(--accent);
                    border-radius: 2px 2px 0 0;
                }
                .dashboard-v2 .list-section {
                    background: var(--surface);
                    border: 1px solid var(--border);
                    border-top: none;
                    border-radius: 0 0 18px 18px;
                    overflow: hidden;
                }
                .dashboard-v2 .list-item {
                    padding: 16px 20px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    border-bottom: 1px solid var(--border);
                    gap: 16px;
                }
                .dashboard-v2 .list-item:last-child { border-bottom: none; }
                .dashboard-v2 .list-item .left { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
                .dashboard-v2 .list-item .amount { font-family: var(--mono); font-weight: 600; font-size: 15px; }
                .dashboard-v2 .list-item .feedback {
                    font-size: 13px;
                    color: var(--text-secondary);
                    max-width: 400px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                .dashboard-v2 .list-item .right {
                    display: flex;
                    flex-direction: column;
                    align-items: flex-end;
                    gap: 4px;
                    flex-shrink: 0;
                }
                .dashboard-v2 .date { font-size: 12px; color: var(--text-muted); }
                .dashboard-v2 .stars { display: flex; gap: 1px; }
                .dashboard-v2 .status-badge {
                    font-size: 11px;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 3px 10px;
                    border-radius: 6px;
                }
                .dashboard-v2 .status-paid { color: var(--green); background: var(--green-light); }
                .dashboard-v2 .status-pending { color: var(--amber); background: var(--amber-light); }
                .dashboard-v2 .see-all {
                    display: block;
                    text-align: center;
                    padding: 14px;
                    color: var(--text-muted);
                    font-size: 13px;
                    font-weight: 500;
                    text-decoration: none;
                    border: none;
                    background: none;
                    width: 100%;
                    font-family: var(--font);
                }
                .dashboard-v2 .see-all:hover { color: var(--accent); }
                .dashboard-v2 .empty-row {
                    padding: 16px 20px;
                    color: var(--text-secondary);
                    font-size: 13px;
                }
            `}</style>

            <div className="dashboard-v2">
                <div className="dashboard">
                    <header className="header">
                        <div className="header-left">
                            <h1>{teamMode ? "Team Tip Pool" : `Hey, ${employee?.firstName || "Employee"}`}</h1>
                            <p>{userData?.businessID || "Tiply"} · Employee Dashboard</p>
                        </div>
                        <Link className="edit-link" to="/profile/information">
                            Edit profile <ExternalIcon />
                        </Link>
                    </header>

                    {employee?.accountStatus === "deactivated" && (
                        <p className="warning">This account is deactivated. Please contact your manager or support@tiply.ee.</p>
                    )}
                    {employee?.verified === false && (
                        <p className="warning">This account is unverified. Please contact your manager to complete verification.</p>
                    )}

                    <div className="filter-wrapper" ref={filterRef}>
                        <button className="filter-btn" onClick={() => setFilterOpen((prev) => !prev)} type="button">
                            {filter} <ChevronDown />
                        </button>
                        {filterOpen && (
                            <div className="filter-dropdown">
                                {FILTER_LABELS.map((label) => (
                                    <button
                                        key={label}
                                        className={`filter-option ${label === filter ? "active" : ""}`}
                                        onClick={() => {
                                            setFilter(label);
                                            setFilterOpen(false);
                                        }}
                                        type="button"
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="stats-grid">
                        <div className="stat-card">
                            <div className="icon-dot" style={{ background: "var(--green-light)" }}>💰</div>
                            <span className="label">Total Earnings</span>
                            <div>
                                <span className="value" style={{ color: "var(--green)" }}>€{totalEarnings.toFixed(2)}</span>
                                <div className="suffix">{filter.toLowerCase()}</div>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="icon-dot" style={{ background: "var(--blue-light)" }}>✓</div>
                            <span className="label">Paid Out</span>
                            <div>
                                <span className="value" style={{ color: "var(--blue)" }}>€{paidOut.toFixed(2)}</span>
                                <div className="suffix">{filteredPayouts.length} payouts</div>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="icon-dot" style={{ background: "var(--surface-raised)" }}>🧾</div>
                            <span className="label">Tips Received</span>
                            <div>
                                <span className="value">{tipCount}</span>
                                <div className="suffix">transactions</div>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="icon-dot" style={{ background: "var(--amber-light)" }}>⏳</div>
                            <span className="label">Pending</span>
                            <div>
                                <span className="value" style={{ color: "var(--amber)" }}>€{pending.toFixed(2)}</span>
                                <div className="suffix">awaiting payout</div>
                            </div>
                        </div>
                    </div>

                    <div className="secondary-stats">
                        <div className="sec-stat">
                            <div className="icon-wrap" style={{ background: "var(--green-light)" }}>💸</div>
                            <div>
                                <div className="sec-label">Avg per day</div>
                                <div className="sec-value" style={{ color: "var(--green)" }}>€{avgPerDay}</div>
                            </div>
                        </div>
                        <div className="sec-stat">
                            <div className="icon-wrap" style={{ background: "var(--amber-light)" }}>⭐</div>
                            <div>
                                <div className="sec-label">Avg rating</div>
                                <div className="sec-value" style={{ color: "var(--amber)" }}>{avgRating}/5</div>
                            </div>
                        </div>
                        <div className="sec-stat">
                            <div className="icon-wrap" style={{ background: "var(--blue-light)" }}>📊</div>
                            <div>
                                <div className="sec-label">Tips per day</div>
                                <div className="sec-value" style={{ color: "var(--blue)" }}>{tipsPerDay}</div>
                            </div>
                        </div>
                    </div>

                    <div className="chart-section">
                        <div className="chart-header">
                            <h2>Earnings Trend</h2>
                            <span className="chart-total">€{totalEarnings.toFixed(2)} total</span>
                        </div>
                        <ResponsiveContainer width="100%" height={220}>
                            <AreaChart data={chartData} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="tipGrad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#0d7c5f" stopOpacity={0.15} />
                                        <stop offset="100%" stopColor="#0d7c5f" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid stroke="#eeeef2" vertical={false} />
                                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#9b9bb0" }} axisLine={false} tickLine={false} />
                                <YAxis
                                    tick={{ fontSize: 11, fill: "#9b9bb0" }}
                                    axisLine={false}
                                    tickLine={false}
                                    tickFormatter={(v) => `€${v}`}
                                    width={42}
                                />
                                <Tooltip content={<CustomTooltip />} />
                                <Area
                                    type="monotone"
                                    dataKey="amount"
                                    stroke="#0d7c5f"
                                    strokeWidth={2.5}
                                    fill="url(#tipGrad)"
                                    dot={false}
                                    activeDot={{ r: 5, stroke: "#0d7c5f", strokeWidth: 2, fill: "#fff" }}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>

                    <div className="qr-section">
                        <div className="qr-placeholder">
                            <div ref={qrRef} style={{ width: "100%", height: "100%" }} />
                        </div>
                        <div className="qr-info">
                            <h3>Your Tip QR Code</h3>
                            <p>Print this QR code or share the link below. Customers scan it to tip you directly.</p>
                            <div className="copy-field">
                                <span className="copy-url">{tipLink || "No tip link available"}</span>
                                <button className="copy-btn" onClick={handleCopy} type="button" disabled={!tipLink}>
                                    {copied ? (
                                        <>
                                            <CheckIcon /> Copied
                                        </>
                                    ) : (
                                        <>
                                            <CopyIcon /> Copy
                                        </>
                                    )}
                                </button>
                            </div>
                            <button
                                className="qr-card-download-btn"
                                type="button"
                                disabled={!tipLink}
                                onClick={() => setCardModalOpen(true)}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <path d="M12 3v12" /><path d="m7 10 5 5 5-5" /><path d="M5 21h14" />
                                </svg>
                                Get a ready-to-print tipping card
                            </button>
                        </div>
                    </div>

                    <div className="pending-banner">
                        <div className="pending-banner-header">
                            <h3>⏳ Pending Payout</h3>
                            <span className="pending-total">€{pendingPayouts?.totalAmount?.toFixed(2) || "0.00"}</span>
                        </div>
                        <div className="pending-list">
                            {pendingPayouts?.tips?.length ? (
                                pendingPayouts.tips.slice(0, 5).map((tip) => (
                                    <div key={tip.id} className="pending-item">
                                        <span className="p-amount">€{Number(tip.amount || 0).toFixed(2)}</span>
                                        <span className="p-date">{formatTipDate(tip)}</span>
                                    </div>
                                ))
                            ) : (
                                <div className="pending-item">
                                    <span className="p-amount">No pending payouts.</span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="tabs-container">
                        <div className="tabs-header">
                            <button className={`tab-btn ${activeTab === "tips" ? "active" : ""}`} onClick={() => setActiveTab("tips")} type="button">
                                Recent Tips
                            </button>
                            <button className={`tab-btn ${activeTab === "payouts" ? "active" : ""}`} onClick={() => setActiveTab("payouts")} type="button">
                                Payout History
                            </button>
                        </div>

                        <div className="list-section">
                            {activeTab === "tips" && (
                                <>
                                    {sortedTips.slice(0, 5).length ? (
                                        sortedTips.slice(0, 5).map((tip) => (
                                            <div key={tip.id} className="list-item">
                                                <div className="left">
                                                    <span className="amount" style={{ color: "var(--green)" }}>+€{Number(tip.amount || 0).toFixed(2)}</span>
                                                    <span className="feedback">{tip.feedback || "No feedback"}</span>
                                                </div>
                                                <div className="right">
                                                    <div className="stars">
                                                        {[1, 2, 3, 4, 5].map((star) => (
                                                            <StarIcon key={star} filled={star <= Number(tip.rating || 0)} />
                                                        ))}
                                                    </div>
                                                    <span className="date">{formatTipDate(tip)}</span>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="empty-row">No tips found.</div>
                                    )}
                                    <Link className="see-all" to="/profile/tips">View all tips →</Link>
                                </>
                            )}

                            {activeTab === "payouts" && (
                                <>
                                    {sortedPayouts.slice(0, 5).length ? (
                                        sortedPayouts.slice(0, 5).map((payout) => {
                                            const status = String(payout.status || "pending").toLowerCase();
                                            const statusClass = status === "paid" ? "status-paid" : "status-pending";
                                            return (
                                                <div key={payout.id} className="list-item">
                                                    <div className="left">
                                                        <span className="amount">€{Number(payout.totalAmount || 0).toFixed(2)}</span>
                                                    </div>
                                                    <div className="right">
                                                        <span className={`status-badge ${statusClass}`}>{status}</span>
                                                        <span className="date">{formatPayoutDate(payout)}</span>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <div className="empty-row">No payouts found.</div>
                                    )}
                                    <Link className="see-all" to="/profile/payouts">View all payouts →</Link>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <TippingCardDownloadModal
                open={cardModalOpen}
                onClose={() => setCardModalOpen(false)}
                tipLink={tipLink}
                targetName={cardTargetName}
                isTeam={teamMode}
            />
        </EmployeeLayoutV2>
    );
}
